import { BRAND } from './brand';

export const COMPANY_EMAIL = 'info@luminarybs.com';
export const COMPANY_EMAIL_HREF = 'mailto:info@luminarybs.com';

/** İletişim formu — hash ile doğrudan forma kaydırma ve odak */
export const CONTACT_FORM_ID = 'iletisim-formu';
export const CONTACT_PATH = `/iletisim#${CONTACT_FORM_ID}`;

export function contactPathWithSubject(konu: string): string {
  const params = new URLSearchParams({ konu });
  return `/iletisim?${params.toString()}#${CONTACT_FORM_ID}`;
}

export const CONTACT_FORM_EMAIL =
  import.meta.env.VITE_CONTACT_FORM_EMAIL || COMPANY_EMAIL;

export type ContactFormData = {
  name: string;
  email: string;
  phone: string;
  company: string;
  subject: string;
  message: string;
};

export type ContactSubmitResult =
  | { ok: true }
  | { ok: false; code: 'activation' | 'network' | 'server'; message: string };

async function submitViaWeb3Forms(
  data: ContactFormData,
  accessKey: string,
): Promise<ContactSubmitResult> {
  const res = await fetch('https://api.web3forms.com/submit', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      access_key: accessKey,
      subject: `${BRAND.name} İletişim: ${data.subject}`,
      from_name: data.name,
      name: data.name,
      email: data.email,
      phone: data.phone || '-',
      company: data.company || '-',
      message: [
        `Konu: ${data.subject}`,
        `Telefon: ${data.phone || '-'}`,
        `Şirket: ${data.company || '-'}`,
        '',
        data.message,
      ].join('\n'),
    }),
  });

  if (!res.ok) {
    return { ok: false, code: 'network', message: `HTTP ${res.status}` };
  }

  const result = (await res.json()) as { success?: boolean; message?: string };
  if (result.success) return { ok: true };

  return {
    ok: false,
    code: 'server',
    message: result.message || 'Form gönderilemedi.',
  };
}

async function submitViaFormSubmit(data: ContactFormData): Promise<ContactSubmitResult> {
  const res = await fetch(`https://formsubmit.co/ajax/${CONTACT_FORM_EMAIL}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      name: data.name,
      email: data.email,
      phone: data.phone,
      company: data.company,
      subject: data.subject,
      message: data.message,
      _replyto: data.email,
      _subject: `${BRAND.name} İletişim: ${data.subject}`,
      _template: 'table',
      _captcha: 'false',
    }),
  });

  if (!res.ok) {
    return { ok: false, code: 'network', message: `HTTP ${res.status}` };
  }

  const result = (await res.json()) as { success?: string | boolean; message?: string };
  if (result.success === true || result.success === 'true') {
    return { ok: true };
  }

  const message = result.message || 'Mesaj gönderilemedi.';
  if (/activation|activate form/i.test(message)) {
    return { ok: false, code: 'activation', message };
  }

  return { ok: false, code: 'server', message };
}

const CONTACT_API_URL = `${import.meta.env.BASE_URL}api/contact.php`;

async function submitViaPhp(data: ContactFormData): Promise<ContactSubmitResult | null> {
  try {
    const res = await fetch(CONTACT_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data),
    });
    if (res.status === 404) return null;
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string };
      return { ok: false, code: 'server', message: err.error ?? `HTTP ${res.status}` };
    }
    const result = (await res.json()) as { ok?: boolean; error?: string };
    if (result.ok) return { ok: true };
    return { ok: false, code: 'server', message: result.error ?? 'Form gönderilemedi.' };
  } catch {
    return null;
  }
}

export async function submitContactForm(data: ContactFormData): Promise<ContactSubmitResult> {
  const phpResult = await submitViaPhp(data);
  if (phpResult) return phpResult;

  const customUrl = import.meta.env.VITE_CONTACT_FORM_URL;
  const web3Key = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY;

  if (web3Key) {
    return submitViaWeb3Forms(data, web3Key);
  }

  if (customUrl) {
    try {
      const body = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => body.append(key, value));
      const res = await fetch(customUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) return { ok: true };
      return { ok: false, code: 'server', message: `HTTP ${res.status}` };
    } catch {
      return { ok: false, code: 'network', message: 'Bağlantı kurulamadı.' };
    }
  }

  try {
    return await submitViaFormSubmit(data);
  } catch {
    return { ok: false, code: 'network', message: 'Bağlantı kurulamadı.' };
  }
}

export function buildMailtoLink(data: ContactFormData, email = CONTACT_FORM_EMAIL): string {
  const subject = encodeURIComponent(`${BRAND.name} İletişim: ${data.subject || 'Genel'}`);
  const body = encodeURIComponent(
    `Ad Soyad: ${data.name}\nE-posta: ${data.email}\nTelefon: ${data.phone || '-'}\nŞirket: ${data.company || '-'}\n\nMesaj:\n${data.message}`,
  );
  return `mailto:${email}?subject=${subject}&body=${body}`;
}
